import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { navLinks } from '@config';
import { KEY_CODES } from '@utils';

const StyledMenu = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: block;
  }
`;

const StyledHamburgerButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    ${({ theme }) => theme.mixins.flexCenter};
    position: relative;
    z-index: 100;
    margin-right: -10px;
    padding: 10px;
    border: 0;
    background-color: transparent;
    color: inherit;
    text-transform: none;
    transition-timing-function: linear;
    transition-duration: 0.15s;
    transition-property: opacity, filter;
  }

  .ham-box {
    display: inline-block;
    position: relative;
    width: var(--hamburger-width);
    height: 18px;
  }

  .ham-box-inner {
    position: absolute;
    top: 50%;
    right: 0;
    width: var(--hamburger-width);
    height: 2px;
    border-radius: var(--border-radius);
    background-color: ${props => (props.menuOpen ? '#ff2a2a' : '#ffffff')};
    box-shadow: ${props => (props.menuOpen ? '0 0 8px #ff2a2a' : 'none')};
    transition-duration: 0.22s;
    transition-property: transform, background-color, box-shadow;
    transition-delay: ${props => (props.menuOpen ? `0.12s` : `0s`)};
    transform: rotate(${props => (props.menuOpen ? `225deg` : `0deg`)});
    transition-timing-function: cubic-bezier(
      ${props => (props.menuOpen ? `0.215, 0.61, 0.355, 1` : `0.55, 0.055, 0.675, 0.19`)}
    );
    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      left: auto;
      right: 0;
      width: var(--hamburger-width);
      height: 2px;
      border-radius: 4px;
      background-color: ${props => (props.menuOpen ? '#ff2a2a' : '#ffffff')};
      box-shadow: ${props => (props.menuOpen ? '0 0 8px #ff2a2a' : 'none')};
      transition-timing-function: ease;
      transition-duration: 0.15s;
      transition-property: transform, background-color, box-shadow;
    }
    &:before {
      width: ${props => (props.menuOpen ? `100%` : `120%`)};
      top: ${props => (props.menuOpen ? `0` : `-10px`)};
      opacity: ${props => (props.menuOpen ? 0 : 1)};
      transition: ${({ menuOpen }) =>
    menuOpen ? 'var(--ham-before-active)' : 'var(--ham-before)'};
    }
    &:after {
      width: ${props => (props.menuOpen ? `100%` : `80%`)};
      bottom: ${props => (props.menuOpen ? `0` : `-10px`)};
      transform: rotate(${props => (props.menuOpen ? `-90deg` : `0`)});
      transition: ${({ menuOpen }) => (menuOpen ? 'var(--ham-after-active)' : 'var(--ham-after)')};
    }
  }
`;

const StyledSidebar = styled.aside`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    padding: 50px 20px 120px;
    width: min(60vw, 320px);
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    touch-action: pan-y;
    outline: 0;
    background: rgba(8, 8, 12, 0.85);
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
    border-left: 1px solid rgba(255, 42, 42, 0.25);
    box-shadow: -10px 0px 30px -10px rgba(0, 0, 0, 0.7), -5px 0 15px rgba(255, 42, 42, 0.08);
    z-index: 99;
    transform: translateX(${props => (props.menuOpen ? 0 : 100)}vw);
    visibility: ${props => (props.menuOpen ? 'visible' : 'hidden')};
    transition: var(--transition);

    @media (max-height: 500px) or (orientation: landscape) {
      padding: 30px 20px 180px;
    }
  }

  nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-direction: column;
    color: var(--lightest-slate);
    font-family: var(--font-mono);
    text-align: center;
  }

  ol {
    padding: 0;
    margin: 0;
    list-style: none;
    width: 100%;

    li {
      position: relative;
      margin: 0 auto 24px;
      counter-increment: item 1;
      font-size: clamp(var(--fz-sm), 4vw, var(--fz-lg));

      @media (max-width: 600px) {
        margin: 0 auto 16px;
      }

      &:before {
        content: '0' counter(item) '.';
        display: block;
        margin-bottom: 5px;
        color: #ff4a4a;
        font-size: var(--fz-sm);
        font-family: var(--font-mono);
      }
    }

    a {
      ${({ theme }) => theme.mixins.link};
      width: 100%;
      padding: 6px 10px;
      word-break: break-word;
      font-weight: 500;
      color: var(--lightest-slate);
      transition: var(--transition);

      @media (hover: hover) {
        &:hover {
          color: #ff2a2a;
          text-shadow: 0 0 5px rgba(255, 42, 42, 0.3);
        }
      }
      &:active {
        color: #ff2a2a;
        text-shadow: 0 0 5px rgba(255, 42, 42, 0.3);
      }
    }
  }

  .resume-link {
    ${({ theme }) => theme.mixins.bigButton};
    padding: 14px 20px;
    margin: 30px auto 0;
    width: max-content;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-radius: var(--border-radius);
    color: #ffffff !important;
    border-color: rgba(255, 42, 42, 0.5);
    background: rgba(255, 42, 42, 0.06);
    box-shadow: 0 0 15px rgba(255, 42, 42, 0.15);
    transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);

    @media (hover: hover) {
      &:hover {
        background: rgba(255, 42, 42, 0.15);
        border-color: #ff2a2a;
        box-shadow: 0 0 25px rgba(255, 42, 42, 0.35);
        transform: translateY(-2px);
      }
    }

    &:active {
      background: rgba(255, 42, 42, 0.15);
      border-color: #ff2a2a;
      box-shadow: 0 0 25px rgba(255, 42, 42, 0.35);
      transform: translateY(-2px);
    }
  }
`;

const Menu = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const buttonRef = useRef(null);
  const navRef = useRef(null);

  let menuFocusables;
  let firstFocusableEl;
  let lastFocusableEl;

  const setFocusables = () => {
    menuFocusables = [buttonRef.current, ...Array.from(navRef.current.querySelectorAll('a'))];
    firstFocusableEl = menuFocusables[0];
    lastFocusableEl = menuFocusables[menuFocusables.length - 1];
  };

  const handleBackwardTab = e => {
    if (document.activeElement === firstFocusableEl) {
      e.preventDefault();
      lastFocusableEl.focus();
    }
  };

  const handleForwardTab = e => {
    if (document.activeElement === lastFocusableEl) {
      e.preventDefault();
      firstFocusableEl.focus();
    }
  };

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11: {
        setMenuOpen(false);
        break;
      }

      case KEY_CODES.TAB: {
        if (menuFocusables && menuFocusables.length === 1) {
          e.preventDefault();
          break;
        }
        if (e.shiftKey) {
          handleBackwardTab(e);
        } else {
          handleForwardTab(e);
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  const onResize = e => {
    if (e.currentTarget.innerWidth > 900) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    setFocusables();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const wrapperRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const listener = event => {
      if (
        !wrapperRef.current ||
        wrapperRef.current.contains(event.target) ||
        (sidebarRef.current && sidebarRef.current.contains(event.target))
      ) {
        return;
      }
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, []);

  // Block background scroll when the menu is open, while keeping the side menu scrollable
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const preventDefault = e => {
      if (sidebarRef.current && sidebarRef.current.contains(e.target)) {
        return;
      }
      if (e.cancelable) {
        e.preventDefault();
      }
    };

    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventDefault, { passive: false });
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.removeEventListener('touchmove', preventDefault);
    };
  }, [menuOpen]);

  return (
    <StyledMenu>
      <Helmet>
        <body className={menuOpen ? 'blur hidden' : ''} />
      </Helmet>

      <div ref={wrapperRef}>
        <StyledHamburgerButton
          onClick={toggleMenu}
          menuOpen={menuOpen}
          ref={buttonRef}
          aria-label="Menu"
        >
          <div className="ham-box">
            <div className="ham-box-inner" />
          </div>
        </StyledHamburgerButton>

        {typeof window !== 'undefined' &&
          createPortal(
            <StyledSidebar
              menuOpen={menuOpen}
              aria-hidden={!menuOpen}
              tabIndex={menuOpen ? 1 : -1}
              ref={sidebarRef}
            >
              <nav ref={navRef}>
                {navLinks && (
                  <ol>
                    {navLinks.map(({ url, name }, i) => (
                      <li key={i}>
                        <Link to={url} onClick={() => setMenuOpen(false)}>
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}

                <a href="/Noor-Ul-Baseer's_Resume.pdf" className="resume-link">
                  Resume
                </a>
              </nav>
            </StyledSidebar>,
            document.body,
          )}
      </div>
    </StyledMenu>
  );
};

export default Menu;
